/**
 * Cross-tab payment signalling. PayPal approval happens in a SEPARATE tab (the
 * approve URL opens with target `_blank`, and the return lands on the callback
 * route there), so the tab that opened the PaymentDialog never learns the payment
 * finished. This broadcasts the outcome to every same-origin tab so the original
 * PaymentDialog can auto-close and refresh balances.
 *
 * Uses BroadcastChannel where available, with a localStorage `storage`-event
 * fallback for older browsers.
 */

export type PaymentResult = {
  /** DataCentral invoice id the payment settled. */
  invoiceId: string;
  status: 'success' | 'error' | 'cancelled';
};

const CHANNEL = 'vn-payment-result';
const LS_KEY = 'vn:payment-result';

/**
 * Announce a payment outcome to all same-origin tabs. Uses EXACTLY ONE channel:
 * BroadcastChannel when supported, else a localStorage storage-event. Publishing
 * on both would deliver the event twice (double toasts / double close).
 */
export function publishPaymentResult(result: PaymentResult): void {
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.postMessage(result);
    bc.close();
    return; // delivered — do NOT also fire the localStorage fallback
  } catch {
    /* BroadcastChannel unsupported — fall through to localStorage */
  }
  try {
    // Writing then clearing fires a `storage` event in OTHER tabs (never the
    // writer's own), carrying the payload as newValue on the set.
    localStorage.setItem(LS_KEY, JSON.stringify(result));
    localStorage.removeItem(LS_KEY);
  } catch {
    /* localStorage unavailable — nothing more to do. */
  }
}

/** Subscribe to payment outcomes from other tabs. Returns an unsubscribe fn.
 *  Mirrors publish: BroadcastChannel when available, else storage — never both,
 *  so a single publish delivers a single callback. */
export function subscribePaymentResult(cb: (r: PaymentResult) => void): () => void {
  try {
    const bc = new BroadcastChannel(CHANNEL);
    bc.onmessage = (e) => cb(e.data as PaymentResult);
    return () => bc.close();
  } catch {
    /* BroadcastChannel unsupported — fall through to storage */
  }
  const onStorage = (e: StorageEvent) => {
    if (e.key === LS_KEY && e.newValue) {
      try {
        cb(JSON.parse(e.newValue) as PaymentResult);
      } catch {
        /* malformed payload — ignore */
      }
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}
